import { useCallback, useEffect, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError, api } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { useSettingsAcl } from "../../lib/useSettingsAcl";
import { SettingsAccessDenied } from "../../SettingsLayout";

export type HeroOutboundStatus = {
  disabled: boolean;
  env_forced: boolean;
  db_disabled: boolean;
  env_key: string;
};

export async function getHeroOutboundControl(): Promise<HeroOutboundStatus> {
  return api.get<HeroOutboundStatus>("/hero/outbound-control");
}

export async function setHeroOutboundControl(disabled: boolean): Promise<HeroOutboundStatus> {
  return api.put<HeroOutboundStatus>("/hero/outbound-control", { disabled });
}

export function HeroOutboundControlSection() {
  const acl = useSettingsAcl();
  const [status, setStatus] = useState<HeroOutboundStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setStatus(await getHeroOutboundControl());
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Hero-Verbindungsstatus konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!acl.loading && !acl.canAuftraege) {
    return <SettingsAccessDenied />;
  }

  async function toggle(disabled: boolean) {
    const label = disabled
      ? "Hero-Verbindung wirklich kappen? Sync und Live-Calls zu Hero stoppen."
      : "Hero-Verbindung wieder aktivieren?";
    if (!window.confirm(label)) return;

    setSaving(true);
    setError(null);
    try {
      const next = await setHeroOutboundControl(disabled);
      setStatus(next);
      toast.success(
        next.disabled
          ? "Hero-Verbindung ist gekappt. Es gehen keine Anfragen mehr an Hero."
          : "Hero-Verbindung ist wieder aktiv.",
      );
      // /me-Cache / Banner: harter Reload der User-Infos via Seiten-Reload unnötig –
      // Banner liest hero_outbound_disabled aus /me beim nächsten Refresh.
      window.dispatchEvent(new Event("eh-hero-outbound-changed"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Umschalten fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="space-y-4 p-4 sm:p-5">
      <div>
        <h2 className="text-base font-semibold text-label">Hero-Verbindung (Kill-Switch)</h2>
        <p className="mt-1 text-sm text-label-secondary">
          Kappt sofort alle ausgehenden Anfragen an Hero (Live-API und geplante Sync-Jobs).
          Die App arbeitet weiter mit lokalen Shadow-Daten, wo vorhanden.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Status wird geladen…
        </div>
      ) : null}

      {error ? <Alert variant="error">{error}</Alert> : null}

      {status ? (
        <div className="space-y-3">
          <Alert variant={status.disabled ? "warning" : "info"}>
            {status.disabled
              ? "Hero-Verbindung ist derzeit gekappt – keine Syncs, keine Live-Calls."
              : "Hero-Verbindung ist aktiv – Syncs und Live-Calls laufen normal."}
            {status.env_forced
              ? ` Erzwungen über ENV ${status.env_key}=true (kann hier nicht deaktiviert werden).`
              : null}
          </Alert>

          <div className="flex flex-wrap gap-2">
            {status.disabled ? (
              <Button
                type="button"
                variant="primary"
                disabled={saving || status.env_forced}
                onClick={() => void toggle(false)}
              >
                {saving ? "Bitte warten…" : "Hero-Verbindung wieder aktivieren"}
              </Button>
            ) : (
              <Button
                type="button"
                variant="danger"
                disabled={saving}
                onClick={() => void toggle(true)}
              >
                {saving ? "Bitte warten…" : "Hero-Verbindung kappen"}
              </Button>
            )}
            <Button type="button" variant="secondary" disabled={saving || loading} onClick={() => void load()}>
              Status aktualisieren
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
